import {
  PushPin,
  PushPinOutlined,
  SearchOutlined,
  ViewColumnOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Badge,
  Button,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  GridPinnedColumnPosition,
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
  GridToolbarExport,
  GridToolbarQuickFilter,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { JSX, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { COLUMNS_HIDDEN_FROM_COLUMN_PANEL, COLUMNS_NOT_HIDEABLE } from "./ColumnsNotHideable";
import { useGridApiContext } from "@mui/x-data-grid-premium";

interface IGridToolbarProps {
  additionalFilters?: JSX.Element;
  additionalActions?: JSX.Element;
  columnFiltersVisible: boolean;
  setColumnFiltersVisible: (visible: boolean) => void;
  mandatoryColumnsVisibility: boolean;
}
declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    additionalFilters?: JSX.Element;
    additionalActions?: JSX.Element;
    columnFiltersVisible: boolean;
    setColumnFiltersVisible: (visible: boolean) => void;
    mandatoryColumnsVisibility: boolean;
  }
}

export function TableToolbar(props: IGridToolbarProps) {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();

  const {
    additionalFilters,
    additionalActions,
    columnFiltersVisible,
    setColumnFiltersVisible,
    mandatoryColumnsVisibility,
  } = props;

  const preferencePanelState = useGridSelector(apiRef, gridPreferencePanelStateSelector);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  return (
    <Stack
      direction="row"
      justifyContent={"space-between"}
      px={2}
      py={2}
      sx={(t) => ({ borderTop: "1px solid", borderBottom: "1px solid", borderColor: t.palette.divider })}
    >
      <Stack direction="row" gap={1}>
        <GridToolbarQuickFilter
          variant="outlined"
          sx={(t) => ({
            flexShrink: 0,
            pb: 0,
            ".MuiOutlinedInput-root": {
              width: 120,
              mt: 0,
              ...t.helpers(t).baseTransition,
              "&.Mui-focused": {
                width: 220,
              },
            },
          })}
          slotProps={{
            input: {
              sx: { mt: 0 },
              startAdornment: null,
              endAdornment: (
                <InputAdornment position="end">
                  <SearchOutlined />
                </InputAdornment>
              ),
            },
          }}
          placeholder={t("common:search")}
          size="small"
        />
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {additionalFilters}
          <Button
            size="small"
            onClick={() => setColumnFiltersVisible(!columnFiltersVisible)}
            sx={{ py: "3px" }}
            endIcon={<Switch size="small" value={columnFiltersVisible} checked={columnFiltersVisible} />}
          >
            More filters
          </Button>
        </Stack>
      </Stack>
      <Stack direction="row" gap={1} alignItems="flex-start">
        {!mandatoryColumnsVisibility && (
          <Badge
            color="info"
            variant="dot"
            invisible={Object.values(columnVisibilityModel).filter((v) => v === false).length === 0}
          >
            <IconButton
              id="columnPreferencesPanelAnchor"
              size="small"
              onClick={(e) => apiRef.current.showPreferences(GridPreferencePanelsValue.columns)}
              color={
                preferencePanelState.open && preferencePanelState.openedPanelValue === GridPreferencePanelsValue.columns
                  ? "primary"
                  : "neutral"
              }
            >
              <ViewColumnOutlined />
            </IconButton>
          </Badge>
        )}
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          slotProps={{ button: { variant: "outlined", sx: { alignSelf: "flex-start" } } }}
        />
        {additionalActions}
      </Stack>
    </Stack>
  );
}

export const CustomColumnsPanel = () => {
  const apiRef = useGridApiContext();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const preferencePanelState = useGridSelector(apiRef, gridPreferencePanelStateSelector);

  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const toggleColumnVisibility = (field: string, visible: boolean) => {
    apiRef.current.setColumnVisibility(field, columnVisibilityModel[field] === false);
  };

  const toggleColumnPin = (field: string) => {
    if (!apiRef.current.isColumnPinned(field)) apiRef.current.pinColumn(field, GridPinnedColumnPosition.LEFT);
    else apiRef.current.unpinColumn(field);
  };

  const hideableColumns = useMemo(
    () => columns.filter((col) => col.hideable && !COLUMNS_HIDDEN_FROM_COLUMN_PANEL.includes(col.field)),
    [columns]
  );

  return (
    <Menu
      open={preferencePanelState.open}
      onClose={apiRef.current.hidePreferences}
      anchorEl={() => document.querySelector("#columnPreferencesPanelAnchor") as HTMLElement}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {hideableColumns.map((column) => (
        <ListItem
          key={column.field}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            pr: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <ListItemText>{column.headerName}</ListItemText>
          <Stack direction="row">
            <IconButton
              size="small"
              onClick={(e) => toggleColumnVisibility(column.field, !(columnVisibilityModel[column.field] ?? true))}
              disabled={COLUMNS_NOT_HIDEABLE.includes(column.field)}
              sx={{
                "&.Mui-disabled": {
                  opacity: 0,
                },
              }}
            >
              {columnVisibilityModel[column.field] === false ? (
                <VisibilityOffOutlined sx={{ opacity: 0.5 }} />
              ) : (
                <VisibilityOutlined color="primary" />
              )}
            </IconButton>
            <IconButton size="small" onClick={(e) => toggleColumnPin(column.field)}>
              {apiRef.current.isColumnPinned(column.field) ? (
                <PushPin color="primary" />
              ) : (
                <PushPinOutlined sx={{ opacity: 0.5 }} />
              )}
            </IconButton>
          </Stack>
        </ListItem>
      ))}
    </Menu>
  );
};
